import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import RatingBadge from './RatingBadge';
import { BadgeIcon } from './UserBadges';
import ProfileReviewsInline from './ProfileReviewsInline';
import ProfileCompleteness from './ProfileCompleteness';
import BookingModal from './BookingModal';
import SessionRequestModal from './SessionRequestModal';
import {
  MapPin, Edit2, UserPlus, UserMinus, Mail, Calendar, Globe,
  Users, Linkedin, Twitter, Share2, CheckCircle, ArrowRight, ShoppingBag, CalendarPlus,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  if (url.includes('preview.emergentagent.com/api/uploads')) {
    const match = url.match(/\/api\/uploads\/.+/);
    if (match) return `${BACKEND_URL}${match[0]}`;
  }
  return url;
};

// Brief Jun 16 — bug fix §1 : social URLs typed without scheme by the user
// (e.g. "youtube.com/channel/x") are treated as relative by the browser and
// silently redirect to /app/home. Normalise to https:// when no scheme present.
const normaliseExternalUrl = (url) => {
  if (!url) return '';
  const u = String(url).trim();
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('//')) return `https:${u}`;
  if (u.startsWith('mailto:') || u.startsWith('tel:')) return u;
  return `https://${u}`;
};

/* ──────────────────────────────────────────────────────────────────────────
   Social row — discreet rounded icons (28px), neutral background.
   Was previously stacked under the avatar with brand colors → relocated as a
   dedicated row below the badges (cf. brief Jun 9 2026 §4).
   ────────────────────────────────────────────────────────────────────────── */
const SocialRow = ({ profile }) => {
  const links = [
    profile.linkedin_url && { href: normaliseExternalUrl(profile.linkedin_url), label: 'LinkedIn', icon: <Linkedin className="h-3.5 w-3.5" /> },
    profile.twitter_url && { href: normaliseExternalUrl(profile.twitter_url), label: 'Twitter / X', icon: <Twitter className="h-3.5 w-3.5" /> },
    profile.youtube_url && {
      href: normaliseExternalUrl(profile.youtube_url), label: 'YouTube',
      icon: <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
    },
    profile.instagram_url && {
      href: normaliseExternalUrl(profile.instagram_url), label: 'Instagram',
      icon: <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c2.717 0 3.056.01 4.122.06 1.065.05 1.79.217 2.428.465.66.254 1.216.598 1.772 1.153.509.5.902 1.105 1.153 1.772.247.637.415 1.363.465 2.428.047 1.066.06 1.405.06 4.122 0 2.717-.01 3.056-.06 4.122-.05 1.065-.218 1.79-.465 2.428a4.883 4.883 0 01-1.153 1.772c-.5.508-1.105.902-1.772 1.153-.637.247-1.363.415-2.428.465-1.066.047-1.405.06-4.122.06-2.717 0-3.056-.01-4.122-.06-1.065-.05-1.79-.218-2.428-.465a4.89 4.89 0 01-1.772-1.153 4.904 4.904 0 01-1.153-1.772c-.248-.637-.415-1.363-.465-2.428C2.013 15.056 2 14.717 2 12c0-2.717.01-3.056.06-4.122.05-1.066.217-1.79.465-2.428a4.88 4.88 0 011.153-1.772A4.897 4.897 0 015.45 2.525c.638-.248 1.362-.415 2.428-.465C8.944 2.013 9.283 2 12 2zm0 5a5 5 0 100 10 5 5 0 000-10zm6.5-.25a1.25 1.25 0 10-2.5 0 1.25 1.25 0 002.5 0zM12 9a3 3 0 110 6 3 3 0 010-6z"/></svg>,
    },
    profile.facebook_url && {
      href: normaliseExternalUrl(profile.facebook_url), label: 'Facebook',
      icon: <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/></svg>,
    },
    profile.website && { href: normaliseExternalUrl(profile.website), label: 'Site web', icon: <Globe className="h-3.5 w-3.5" /> },
  ].filter(Boolean);
  if (!links.length) return null;
  return (
    <div className="flex items-center gap-2 mt-3" data-testid="hero-social-row">
      {links.map((l) => (
        <a
          key={l.label}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          title={l.label}
          data-testid={`hero-social-${l.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
          className="w-7 h-7 rounded-full bg-gray-50 dark:bg-gray-700/60 border border-gray-200/70 dark:border-gray-600/60 text-gray-600 dark:text-gray-200 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          {l.icon}
        </a>
      ))}
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
   "Prochain créneau" block — pulled from /api/calendar/events/public/{userId}
   (same source as the calendar grid). Hidden if no upcoming event.
   ────────────────────────────────────────────────────────────────────────── */
const NextSlotBlock = ({ userId, showAsOwner, forceFreeSessions, onBookClick }) => {
  const navigate = useNavigate();
  const [next, setNext] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/calendar/events/public/${userId}`)
      .then(({ data }) => {
        if (cancelled) return;
        const now = Date.now();
        const items = (data.events || [])
          .map((e) => ({ ...e, _start: e.start_time ? new Date(e.start_time) : null }))
          .filter((e) => e._start && e._start.getTime() > now
            && (e.type === 'public_event' || e.type === 'public_slot' || e.type === 'public_series'))
          .sort((a, b) => a._start - b._start);
        setNext(items[0] || null);
      })
      .catch(() => setNext(null));
    return () => { cancelled = true; };
  }, [userId]);

  if (!next) return null;

  const dateStr = next._start.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const timeStr = next._start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const isFree = !!next.is_free || forceFreeSessions;
  const priceLabel = isFree
    ? 'Gratuit pendant la beta'
    : `${next.price || 0} ${next.currency || 'CHF'}`;

  return (
    <div
      className="mt-4 flex items-center gap-3 p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/60 border border-gray-200/70 dark:border-gray-600/40"
      data-testid="hero-next-slot"
    >
      <div className="h-8 w-8 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center shrink-0">
        <Calendar className="h-4 w-4 text-purple-600 dark:text-purple-300" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate">
          {next.title || 'Session disponible'} <span className="text-gray-500 dark:text-gray-400">· {dateStr} · {timeStr}</span>
        </p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400">{priceLabel}</p>
      </div>
      {!showAsOwner && (
        <button
          type="button"
          onClick={() => (onBookClick ? onBookClick() : navigate(`/app/profile/${userId}?book=1`))}
          className="inline-flex items-center gap-1 text-[12px] font-semibold rounded-lg px-3 py-1.5 text-white hover:opacity-90 shrink-0"
          style={{ backgroundColor: '#7F77DD' }}
          data-testid="hero-next-slot-book"
        >
          Réserver <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
   Confetti — pure CSS celebration on 100% profile completion.
   ────────────────────────────────────────────────────────────────────────── */
const Confetti = () => {
  const colors = ['#7F77DD', '#1D9E75', '#ec4899', '#facc15', '#06b6d4'];
  const pieces = Array.from({ length: 30 }, (_, i) => ({
    left: (i * 7 + (i % 3) * 13) % 100,
    delay: (i % 6) * 0.12,
    color: colors[i % colors.length],
    rotate: (i * 31) % 360,
    size: 6 + (i % 3) * 2,
  }));
  return (
    <div className="pointer-events-none fixed inset-0 z-[60] overflow-hidden" data-testid="profile-celebration-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="absolute top-0 block animate-[osConfetti_2.6s_ease-out_forwards]"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size * 1.6}px`,
            backgroundColor: p.color,
            transform: `rotate(${p.rotate}deg)`,
            animationDelay: `${p.delay}s`,
            borderRadius: '2px',
          }}
        />
      ))}
      <style>{`
        @keyframes osConfetti {
          0%   { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

const ProfileHeader = ({
  profile, userId, badges, showAsOwner, isEditing, editData, setEditData,
  bannerStyle, onFollow, followLoading, onShowFollowModal, onShowTipModal,
  onShowShareModal, onShowEditModal, onShowReviewsModal,
}) => {
  const navigate = useNavigate();
  // Brief LOT1 — route unifiée : /app/profile/{id}?book=1 ouvre la modal de
  // réservation (cible de l'ancien /app/booking/{id} et des CTA legacy).
  const [searchParams, setSearchParams] = useSearchParams();

  // Sprint 1.0c Jun 9 — public_config + celebration state.
  const [forceFreeSessions, setForceFreeSessions] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  // Brief B5 P0 Bug 4 — booking modal opens in place (no /app/booking/:userId navigation)
  const [bookingOpen, setBookingOpen] = useState(false);
  // Brief Jul 7 2026 §4 — modal demande de session 1-to-1 spontanée.
  const [requestSessionOpen, setRequestSessionOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get('book') === '1' && !showAsOwner) {
      setBookingOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('book');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, showAsOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/config/public`)
      .then(({ data }) => { if (!cancelled) setForceFreeSessions(data.force_free_sessions !== false); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // 100% profile celebration — owner only, fires once via backend flag.
  useEffect(() => {
    if (!showAsOwner) return;
    let cancelled = false;
    axios.get(`${API}/home/checklist`, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        if ((data?.pct || 0) >= 100 && !profile.profile_celebration_shown) {
          setShowCelebration(true);
          // Persist the "shown" flag so we never replay it.
          axios.post(`${API}/users/profile/celebration-shown`, {}, { withCredentials: true })
            .catch(() => {});
          // Lazy import toast to avoid blocking
          import('sonner').then((mod) => mod.toast.success('Profil complet — tu es prêt à être découvert !'));
          // Confetti auto-cleanup
          setTimeout(() => { if (!cancelled) setShowCelebration(false); }, 3200);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showAsOwner, profile.profile_celebration_shown]);

  // Sprint 1.0c Jun 9 — tagline jobTitle · primarySkill · city, country
  const primaryExperience = Array.isArray(profile.experience)
    ? profile.experience.find((e) => e && (e.title || e.position || e.role))
    : null;
  const jobTitle = primaryExperience?.title || primaryExperience?.position || primaryExperience?.role || '';
  const primarySkill = Array.isArray(profile.skills) && profile.skills.length > 0
    ? (typeof profile.skills[0] === 'string' ? profile.skills[0] : profile.skills[0]?.name)
    : '';
  const city = profile.city || (profile.location || '').split(',')[0]?.trim() || '';
  const country = profile.country || (profile.location || '').split(',').slice(1).join(',').trim() || '';
  const taglineParts = [jobTitle, primarySkill, [city, country].filter(Boolean).join(', ')].filter(Boolean);

  const skillsForHero = Array.isArray(profile.skills) ? profile.skills.slice(0, 5) : [];
  const skillOverflow = Array.isArray(profile.skills) ? Math.max(0, profile.skills.length - 5) : 0;
  const learningInterests = Array.isArray(profile.learning_interests) ? profile.learning_interests.filter(Boolean) : [];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden mb-6">
      {showCelebration && <Confetti />}

      {/* Banner */}
      <div className="h-40 sm:h-52 relative" style={bannerStyle}>
        {isEditing && (
          <div className="absolute bottom-4 right-4 flex gap-2">
            <Button size="sm" variant="secondary" className="bg-white/90 hover:bg-white"
              onClick={() => {
                const color1 = prompt('Couleur 1 (ex: #667eea):', editData.banner_gradient?.color1 || '#667eea');
                const color2 = prompt('Couleur 2 (ex: #764ba2):', editData.banner_gradient?.color2 || '#764ba2');
                if (color1 && color2) setEditData({ ...editData, banner_gradient: { color1, color2, direction: 'to right' } });
              }}
            >
              <Edit2 className="h-4 w-4 mr-1" />Modifier banniere
            </Button>
          </div>
        )}
      </div>

      <div className="relative px-6 pb-6">
        {/* Avatar — keep position + rating badge as before. Social links MOVED out. */}
        <div className="absolute -top-16 sm:-top-20 left-6">
          <div className="flex flex-col items-center">
            <Avatar className="h-28 w-28 sm:h-36 sm:w-36 border-4 border-white dark:border-gray-800 shadow-xl">
              <AvatarImage src={resolveUrl(profile.avatar)} />
              <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-3xl font-bold">
                {profile.display_name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
              </AvatarFallback>
            </Avatar>
            <div className="mt-2 bg-white dark:bg-gray-700 rounded-full px-3 py-1 shadow-md border border-gray-100 dark:border-gray-600">
              <RatingBadge userId={userId} showOnEmpty={true} size="sm" onClick={() => onShowReviewsModal(true)} />
            </div>
          </div>
        </div>

        {/* CTAs — Sprint 1.0c Jun 9: 3 CTAs only (Message · Suivre · Réserver).
            Owner: Partager + Modifier le profil (kept). */}
        {/* Brief 27 oct LOT H — mobile : la rangée de CTA passe SOUS l'avatar
            (pt-24) au lieu de le chevaucher ; desktop inchangé (pt-4). */}
        <div className="grid grid-cols-2 gap-2 pt-24 sm:pt-4 sm:pl-44 sm:flex sm:flex-wrap sm:justify-end" data-testid="profile-hero-actions">
          {showAsOwner ? (
            <>
              <Button variant="outline" onClick={() => onShowShareModal(true)} data-testid="profile-hero-share-btn">
                <Share2 className="h-4 w-4 mr-2" />Partager
              </Button>
              <Button
                onClick={() => onShowEditModal(true)}
                className="text-white hover:opacity-90"
                style={{ backgroundColor: '#7F77DD' }}
                data-testid="edit-profile-btn"
              >
                <Edit2 className="h-4 w-4 mr-2" />Modifier le profil
              </Button>
            </>
          ) : (
            <>
              {/* Brief 27 oct B.1 — Partager disponible sur TOUT profil (pas seulement le sien) */}
              <Button variant="outline" onClick={() => onShowShareModal(true)} data-testid="profile-share-btn" title="Partager ce profil">
                <Share2 className="h-4 w-4 mr-2" />Partager
              </Button>
              <Button variant="outline" onClick={() => navigate(`/app/messages/${userId}`)} data-testid="hero-cta-message">
                <Mail className="h-4 w-4 mr-2" />Message
              </Button>
              <Button
                variant="outline"
                onClick={onFollow}
                disabled={followLoading}
                data-testid="hero-cta-follow"
              >
                {profile.is_following
                  ? (<><UserMinus className="h-4 w-4 mr-2" />Ne plus suivre</>)
                  : (<><UserPlus className="h-4 w-4 mr-2" />Suivre</>)}
              </Button>
              {/* Brief B5 P1 §5 — bouton Boutique ambre entre Suivre et Réserver
                  (outline, distinct visuellement du primary Réserver). */}
              <Button
                variant="outline"
                onClick={() => navigate(`/app/shop/${userId}`)}
                className="border-amber-500 text-amber-700 hover:bg-amber-50 dark:border-amber-400 dark:text-amber-300 dark:hover:bg-amber-900/20"
                data-testid="hero-cta-shop"
              >
                <ShoppingBag className="h-4 w-4 mr-2" />Boutique
              </Button>
              {/* Brief Jul 7 2026 §4 — demande de session 1-to-1 spontanée
                  (outline violet, avant Réserver) */}
              <Button
                variant="outline"
                onClick={() => setRequestSessionOpen(true)}
                className="border-[#7F77DD] text-[#7F77DD] hover:bg-[#7F77DD]/10 dark:text-[#bcb6f3] dark:border-[#7F77DD] col-span-2 sm:col-auto"
                data-testid="hero-cta-request-session"
              >
                <CalendarPlus className="h-4 w-4 mr-2" />Demander une session
              </Button>
              <Button
                onClick={() => setBookingOpen(true)}
                className="text-white hover:opacity-90 col-span-2 sm:col-auto"
                style={{ backgroundColor: '#7F77DD' }}
                data-testid="book-btn"
              >
                <Calendar className="h-4 w-4 mr-2" />Réserver
              </Button>
            </>
          )}
        </div>

        {/* Name + Tagline + Badges + Social + Skills + Explore + Next slot */}
        <div className="mt-5 sm:mt-24">
          {isEditing ? (
            <input type="text" value={editData.display_name || ''} onChange={(e) => setEditData({ ...editData, display_name: e.target.value })} className="text-2xl sm:text-3xl font-bold text-gray-900 bg-transparent border-b-2 border-purple-300 focus:border-purple-500 outline-none w-full" />
          ) : (
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3" data-testid="hero-name">
              {profile.display_name}
              {badges.length > 0 && (
                <div className="flex items-center gap-1">
                  {badges.slice(0, 3).map((badgeId, idx) => (<BadgeIcon key={`${badgeId}-${idx}`} badgeId={badgeId} size="sm" />))}
                </div>
              )}
            </h1>
          )}

          {/* Tagline — Sprint 1.0c Jun 9 */}
          {taglineParts.length > 0 && (
            <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400" data-testid="hero-tagline">
              {taglineParts.join(' · ')}
            </p>
          )}

          {/* Brief audit beta Feb 2026 (P1 §16) — Pitch d'offre :
              texte pleine largeur, 14px, non italique — se lit comme la
              première ligne d'un CV. */}
          {profile.expert_pitch && (
            <p
              className="mt-2 text-[14px] text-gray-800 dark:text-gray-100 leading-snug"
              data-testid="hero-expert-pitch"
            >
              {profile.expert_pitch}
            </p>
          )}

          {/* Brief audit beta Feb 2026 (P1 §16) — Offer tags rendus comme
              texte discret séparé par points médians (·) plutôt qu'en pills.
              Se lit comme une accroche pro sous le pitch. */}
          {Array.isArray(profile.offer_tags) && profile.offer_tags.length > 0 && (
            <p
              className="mt-1 text-[12px] text-gray-500 dark:text-gray-400"
              data-testid="hero-offer-tags"
            >
              {profile.offer_tags.slice(0, 3).map((tag, idx) => (
                <span key={`offer-${idx}`} data-testid={`hero-offer-tag-${idx}`}>
                  {idx > 0 && <span className="mx-1.5">·</span>}
                  {tag}
                </span>
              ))}
            </p>
          )}

          {/* Badges — Sprint 1.0c Jun 9: épuration. Disponible + sessions + abonnés uniquement. */}
          <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="hero-badges">
            {profile.availability_status === 'available' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" data-testid="hero-badge-available">
                <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span> Disponible
              </span>
            )}
            {profile.sessions_completed > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700/60" data-testid="hero-badge-sessions">
                <CheckCircle className="h-3 w-3 text-purple-500" />
                <strong>{profile.sessions_completed}</strong>&nbsp;session{profile.sessions_completed > 1 ? 's' : ''} réalisée{profile.sessions_completed > 1 ? 's' : ''}
              </span>
            )}
            <button
              onClick={() => onShowFollowModal('followers')}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700/60 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              data-testid="hero-badge-followers"
            >
              <Users className="h-3 w-3 text-purple-500" />
              <strong>{profile.follower_count || 0}</strong>&nbsp;abonné{(profile.follower_count || 0) > 1 ? 's' : ''}
            </button>
            {profile.location && (
              <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                <MapPin className="h-3 w-3" />{profile.location}
              </span>
            )}
          </div>

          {/* Socials — moved out of the avatar column */}
          <SocialRow profile={profile} />

          {/* Skills — Sprint 1.0c Jun 9: max 5 in hero (full list under the fold stays) */}
          {skillsForHero.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid="hero-skills">
              {skillsForHero.map((s, idx) => {
                const label = typeof s === 'string' ? s : (s?.name || '');
                if (!label) return null;
                return (
                  <span
                    key={`${label}-${idx}`}
                    className="px-2 py-0.5 text-[11px]"
                    style={{
                      background: 'var(--color-background-secondary)',
                      border: '0.5px solid var(--color-border-tertiary)',
                      borderRadius: 'var(--border-radius-md)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {label}
                  </span>
                );
              })}
              {skillOverflow > 0 && (
                <span className="text-[11px] text-gray-400" data-testid="hero-skills-more">+ {skillOverflow} autre{skillOverflow > 1 ? 's' : ''}</span>
              )}
            </div>
          )}

          {/* Explore aussi — Sprint 1.0c Jun 9 dual-rôle signal */}
          {learningInterests.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2.5" data-testid="hero-learning-interests">
              <span className="text-[11px] text-gray-400 dark:text-gray-500">Explore aussi&nbsp;:</span>
              {learningInterests.slice(0, 3).map((tag, idx) => (
                <span
                  key={`li-${idx}`}
                  className="px-2 py-0.5 rounded-full text-[11px]"
                  style={{ backgroundColor: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #9FE1CB' }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Prochain créneau — Sprint 1.0c Jun 9 */}
          <NextSlotBlock userId={userId} showAsOwner={showAsOwner} forceFreeSessions={forceFreeSessions} onBookClick={() => setBookingOpen(true)} />

          {/* Brief B5 P0 Bug 4 — booking modal */}
          <BookingModal
            isOpen={bookingOpen}
            onClose={() => setBookingOpen(false)}
            expertUserId={userId}
            expertName={profile.display_name}
          />

          {/* Brief Jul 7 2026 §4 — modal demande 1-to-1 spontanée */}
          <SessionRequestModal
            isOpen={requestSessionOpen}
            onClose={() => setRequestSessionOpen(false)}
            expert={{ user_id: userId, display_name: profile.display_name }}
          />

          {/* Inline Reviews (under-the-fold preview) */}
          <ProfileReviewsInline
            userId={userId}
            isOwnProfile={showAsOwner}
            onOpenReviewsModal={() => onShowReviewsModal && onShowReviewsModal(true)}
          />

          {/* Profile Completeness — owner only — Brief B5 P0 Bug 2b : cliquable */}
          {showAsOwner && <ProfileCompleteness profile={profile} onEdit={() => onShowEditModal(true)} />}
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
